import { BehaviorSubject } from 'rxjs';
import { GoogleLoginComponent } from './google.login/google.login.component';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { FacebookLoginComponent } from './facebook.login/facebook.login.component';
import { CheckLoginStatusService } from './checkLoginStatus.service';

@Component({
  selector: 'ig-social-login',
  templateUrl: './social.login.component.html',
  styleUrls: ['./social.login.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SocialLoginComponent implements OnInit, OnDestroy {
  @Input() mode: 'vertical' | 'horizontal' | 'popup' = 'vertical';
  public googleReady$ = GoogleLoginComponent.googleReady$;
  public profilePicture: string = '';
  public static loginStatus$ = new BehaviorSubject(false);

  // Add static properties for app-level login data
  public static appLoginData: any = null;
  public static isAppLevelLogin = false;

  @ViewChild('google', { static: true })
  public googleLoginComponent: GoogleLoginComponent;
  @ViewChild('facebook', { static: false })
  public facebookLoginComponent: FacebookLoginComponent;

  @Output() onLoginStatusChanged: EventEmitter<boolean> = new EventEmitter();
  @Output() onEmailIconClick: EventEmitter<string> = new EventEmitter();

  private messageListener: (event: MessageEvent) => void;
  static instance: SocialLoginComponent;

  constructor(public  checkLoginStatusService : CheckLoginStatusService) {
    SocialLoginComponent.instance = this
    const statusOfLogin = localStorage.getItem('social.login.googleUser') || localStorage.getItem('social.login.facebookUser') ? true : false;
    SocialLoginComponent.loginStatus$.next(statusOfLogin);
    SocialLoginComponent.loginStatus$.subscribe((status) => {
      this.loginStatusChanged(status);
    });
  }

  ngOnInit() {
    // Check for existing login status
    this.checkInitialLoginStatus();
    // Listen for messages from the parent app
    this.messageListener = (event: MessageEvent) => {
      this.handleAppMessage(event);
    };
    window.addEventListener('message', this.messageListener);

  }

  // Check for existing login data on component initialization
  private checkInitialLoginStatus() {
    // Check if already logged in via app-level OAuth
    if (
      SocialLoginComponent.isAppLevelLogin &&
      SocialLoginComponent.appLoginData
    ) {
      this.profilePicture = SocialLoginComponent.appLoginData.profilePicture;
      return;
    }

  }

  ngOnDestroy() {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
    }
  }

  // Handle messages from the parent app
  private handleAppMessage(event: MessageEvent) {
  try {
    let message;
    
    if (typeof event.data === 'string') {
      // Check if it's URL-encoded data (contains = and & characters)
      if (event.data.includes('=') && event.data.includes('&')) {
        // Parse URL-encoded string
        message = this.parseUrlEncodedData(event.data);
      } else {
        // Try to parse as JSON
        try {
          message = JSON.parse(event.data);
        } catch (jsonError) {
          console.warn('Data is neither JSON nor URL-encoded:', event.data);
          return;
        }
      }
    } else {
      // Data is already an object
      message = event.data;
    }

    if (message.type === 'APP_LOGIN_STATUS_UPDATE') {
      this.updateLoginStatus(message.data);
    }
    
    // Handle other message types based on the parsed data
    // For Facebook login response, you might want to handle it differently
    if (message.signed_request || message.long_lived_token) {
      this.handleFacebookLoginResponse(message);
    }
    
  } catch (error) {
    console.error('Error handling app message:', error);
  }
}

private parseUrlEncodedData(data: string): any {
  const params = new URLSearchParams(data);
  const result: any = {};
  
  for (const [key, value] of params.entries()) {
    // Try to decode URI components
    try {
      result[key] = decodeURIComponent(value);
    } catch (decodeError) {
      result[key] = value;
    }
  }
  
  return result;
}

private handleFacebookLoginResponse(data: any) {
  // Handle Facebook login response
  console.log('Facebook login data received:', {
    user_id: this.extractUserIdFromSignedRequest(data.signed_request),
    access_token: data.long_lived_token,
    expires_in: data.expires_in,
    profilePicture : FacebookLoginComponent.profilePicture
  });
  
  // Process the login data as needed
  if (data.signed_request) {
    const userData = this.parseSignedRequest(data.signed_request);
    this.updateLoginStatus({
      isLoggedIn: true,
      userId: userData.user_id,
      accessToken: data.long_lived_token,
      profilePicture : FacebookLoginComponent.profilePicture,
      provider: 'facebook'
    });
  }
}

private parseSignedRequest(signedRequest: string): any {
  try {
    const [signature, payload] = signedRequest.split('.');
    
    // Decode the payload (it's base64url encoded)
    const decodedPayload = this.base64UrlDecode(payload);
    return JSON.parse(decodedPayload);
  } catch (error) {
    console.error('Error parsing signed request:', error);
    return {};
  }
}

private base64UrlDecode(str: string): string {
  // Add padding if needed
  str += '='.repeat((4 - str.length % 4) % 4);
  
  // Replace URL-safe characters
  str = str.replace(/-/g, '+').replace(/_/g, '/');
  
  // Decode base64
  return atob(str);
}

private extractUserIdFromSignedRequest(signedRequest: string): string | null {
  try {
    const userData = this.parseSignedRequest(signedRequest);
    return userData.user_id || null;
  } catch (error) {
    console.error('Error extracting user ID:', error);
    return null;
  }
}

  // Update login status from app-level OAuth
  private updateLoginStatus(loginData: any) {
    SocialLoginComponent.appLoginData = loginData;
    SocialLoginComponent.isAppLevelLogin = loginData.isLoggedIn;

    // Update individual component static data
    if (loginData.provider === 'google') {
      GoogleLoginComponent.updateFromAppLogin(loginData);
    } else if (loginData.provider === 'facebook') {
      FacebookLoginComponent.updateFromAppLogin(loginData);
    }

    if (loginData.isLoggedIn) {
      this.profilePicture = loginData.profilePicture;
      SocialLoginComponent.loginStatus$.next(true);
    } else {
      console.log('else in social login', loginData);
      
      // Clear both providers when logging out
      FacebookLoginComponent.clearLoginData();
      GoogleLoginComponent.clearLoginData();
      this.profilePicture = JSON.parse(localStorage.getItem('social.login.googleUser') || localStorage.getItem('social.login.facebookUser'))?.profilePicture || '';
      SocialLoginComponent.loginStatus$.next(false);
    }
  }

  // Send login request to parent app
  public requestAppLogin(provider: 'google' | 'facebook') {
    const message = {
      type: 'APP_LOGIN_REQUEST',
      provider: provider,
    };

    // Send message to parent app
    if (window.parent !== window) {
      console.log('request to app login message', message);
      window.parent.postMessage(message, '*');
    }
  }

  // Send logout request to parent app
  public requestAppLogout() {
    const currentProvider = SocialLoginComponent.appLoginData?.provider;
    if (currentProvider) {
      const message = {
        type: 'APP_LOGOUT_REQUEST',
        provider: currentProvider,
      };

      if (window.parent !== window) {
        window.parent.postMessage(message, '*');
      }
    }
  }

  // Updated static getters to handle app-level login
  public static get isUserLoggedIn() {
    if (SocialLoginComponent.isAppLevelLogin) {
      return true;
    }
    return (
      GoogleLoginComponent.isUserLoggedIn ||
      FacebookLoginComponent.isUserLoggedIn
    );
  }

  public static get userId() {
    if (
      SocialLoginComponent.isAppLevelLogin &&
      SocialLoginComponent.appLoginData
    ) {
      return SocialLoginComponent.appLoginData.userId;
    }
    if (GoogleLoginComponent.isUserLoggedIn) {
      return GoogleLoginComponent.googleUserId;
    }
    if (FacebookLoginComponent.isUserLoggedIn) {
      return FacebookLoginComponent.userFacebookId;
    }
    return '';
  }

  public static get profilePicture() {
    console.log('SocialLoginComponent.profilePicture start of get', SocialLoginComponent.instance.checkLoginStatusService.isUserLoggedIn);
    
    if (
      SocialLoginComponent.isAppLevelLogin &&
      SocialLoginComponent.appLoginData
    ) {
      return SocialLoginComponent.appLoginData.profilePicture;
    }
    if (SocialLoginComponent.instance.checkLoginStatusService.isUserLoggedIn) {
      return SocialLoginComponent.instance.checkLoginStatusService.profilePicture;
    }
    return '';
  }

  public static get socialName() {
    if (
      SocialLoginComponent.isAppLevelLogin &&
      SocialLoginComponent.appLoginData
    ) {
      return SocialLoginComponent.appLoginData.provider;
    }
    if (GoogleLoginComponent.isUserLoggedIn) {
      return 'google';
    }
    if (FacebookLoginComponent.isUserLoggedIn) {
      return 'facebook';
    }
    return '';
  }

  public static get userName() {
    if (
      SocialLoginComponent.isAppLevelLogin &&
      SocialLoginComponent.appLoginData
    ) {
      return SocialLoginComponent.appLoginData.userName;
    }
    if (GoogleLoginComponent.isUserLoggedIn) {
      return GoogleLoginComponent.userName;
    }
    if (FacebookLoginComponent.isUserLoggedIn) {
      return `Facebook user ${FacebookLoginComponent.userFacebookId}`;
    }
    return '';
  }

  public static get email() {
    if (
      SocialLoginComponent.isAppLevelLogin &&
      SocialLoginComponent.appLoginData
    ) {
      return SocialLoginComponent.appLoginData.email;
    }
    if (GoogleLoginComponent.isUserLoggedIn) {
      return GoogleLoginComponent.email;
    }
    return '';
  }

  public logout() {
    
      this.requestAppLogout();
    
      // Handle regular webview logout
      if (this.checkLoginStatusService.isUserLoggedIn && this.checkLoginStatusService.provider === 'google') {
        this.googleLoginComponent.logout();
      }
      if (this.checkLoginStatusService.isUserLoggedIn && this.checkLoginStatusService.provider === 'facebook') {
        this.facebookLoginComponent.logout();
      }
    
  }

  public loginStatusChanged(status: boolean) {
    if (!status) {
      console.log('loginStatusChanged in social login', status);
      
      this.profilePicture = JSON.parse(localStorage.getItem('social.login.googleUser') || localStorage.getItem('social.login.facebookUser'))?.profilePicture || '';;
    } else {
      // Set profile picture based on login source
      if (
        SocialLoginComponent.isAppLevelLogin &&
        SocialLoginComponent.appLoginData
      ) {
        this.profilePicture = SocialLoginComponent.appLoginData.profilePicture;
      } else if (this.checkLoginStatusService.isUserLoggedIn && this.checkLoginStatusService.provider === 'google') {
        this.profilePicture = this.checkLoginStatusService.profilePicture;
      } else if (this.checkLoginStatusService.isUserLoggedIn && this.checkLoginStatusService.provider === 'facebook') {
        this.profilePicture = this.checkLoginStatusService.profilePicture;
      }
    }
    this.onLoginStatusChanged.emit(status);
  }

  public initGoogleLogin() {
    // If in app context, request app-level login
    if (window.parent !== window) {
      this.requestAppLogin('google');
    } else {
      // Fallback to regular webview login
      this.googleLoginComponent.initializeGoogle();
    }
  }

  // Method to trigger Facebook login
  public initFacebookLogin() {
    if (window.parent !== window) {
      this.requestAppLogin('facebook');
    } else {
      // Fallback to regular webview login
      if (this.facebookLoginComponent) {
        this.facebookLoginComponent.login();
      }
    }
  }

  public isInWebView(): boolean {
    // Check if we're running inside a webview (parent window exists and is different)
    return (
      window.parent !== window ||
      navigator.userAgent.includes('wv') || // Android WebView
      (navigator.userAgent.includes('Version/') &&
        navigator.userAgent.includes('Mobile/'))
    ); // iOS WebView
  }
}
